import { useCallback } from 'react'
import toast from 'react-hot-toast'

import { useFvNavigate } from '@fv/client-components'
import { useMutation } from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'
import type { RetenderLoadDto, RetenderReason } from '@fv/models'

import { supportMessage } from '@/constants'
import { routes } from '@/routes'
import {
  hasDeclaredValue,
  hasFlatbedDetails,
  hasItemsEntered,
} from '@/utils/shipmentFuncs'
import { shipperFetch } from '@/utils/shipperFetch'

import { useCarrierBookSettings } from '../settings'
import { useRemoveFromList, useUpdateCachedLoad } from '.'
import type { QuoteRequestResponse } from './useQuoteRequestResponses'

const postLoadRetender = (loadId: string) => async (body: RetenderLoadDto) => {
  return shipperFetch<ListShipment>(`/shipments/${loadId}/retender`, {
    method: 'POST',
    body,
  })
}

export function useRetenderLoad(loadId: string) {
  const removeFromList = useRemoveFromList()
  const updateCachedLoad = useUpdateCachedLoad()

  return useMutation(postLoadRetender(loadId), {
    onSuccess: updatedLoad => {
      removeFromList({
        loadId: updatedLoad.loadId,
        queryFilter: ['shipments', 'confirmed'],
      })

      updateCachedLoad({ updatedLoad })
      toast.success('Load retendered successfully.')
    },
  })
}

type RetenderOpts = {
  reason: RetenderReason | null
}

interface RetenderLoadToNewCarrierOpts {
  load: ListShipment
  quote: Pick<QuoteRequestResponse, 'id' | 'carrierId'>
}

export function useRetenderLoadToNewCarrier({
  load,
  quote,
}: RetenderLoadToNewCarrierOpts) {
  const mutation = useRetenderLoad(load.loadId)
  const navigate = useFvNavigate()
  const settingsQuery = useCarrierBookSettings('truckload', [quote.carrierId])

  const retender = useCallback(
    (body: RetenderLoadDto) => {
      mutation.mutateAsync(body).catch(e => {
        toast.error(e?.message ?? `Unable to retender load, ${supportMessage}`)
      })
    },
    [mutation],
  )

  const needsBOLDetails =
    settingsQuery.data.requiresFullBOLDetails && !hasItemsEntered(load)
  const needsFlatbedDetails =
    settingsQuery.data.requiresFlatbedDetails && !hasFlatbedDetails(load)
  const needsDeclaredValue =
    settingsQuery.data.requiresDeclaredValue && !hasDeclaredValue(load)
  const shouldNavigate =
    needsBOLDetails || needsFlatbedDetails || needsDeclaredValue

  const mutate = useCallback(
    (opts: RetenderOpts) => {
      if (shouldNavigate) {
        navigate(
          routes.bookTruckload({
            mode: 'retender',
            quoteRequestId: load.quoteRequestId,
            reason: opts.reason,
            selectedQuotes: { [load.loadId]: quote.id },
          }),
        )
      } else {
        retender({
          quoteId: quote.id,
          reason: opts.reason,
        })
      }
    },
    [
      retender,
      load.loadId,
      navigate,
      load.quoteRequestId,
      shouldNavigate,
      quote.id,
    ],
  )

  return {
    mutate,
    isLoading: settingsQuery.isLoading || mutation.isPending,
    requireReason: (load: Pick<ListShipment, 'selectedQuote' | 'status'>) => {
      return (
        !!load.selectedQuote &&
        (load.status === 'awarded' || load.status === 'confirmed')
      )
    },
  }
}
